import type { ReactNode } from 'react';
import { ContextHeader } from './Headers';

// ------------------------------------------------------------------
// RailPanel — story 2-127 (UI-DS-00 §3.1 / 2026-08-22-app-shell-ui-
// refinement-design.md §3.1, §4.3-4.4). Header row refined in story 2-147sb
// to compose the extracted `ContextHeader` (`Headers.tsx`) instead of
// hand-rolling its own title+action `<div>` — byte-identical classes/markup,
// just named and independently reusable now (P10 §2.6 header kind #4).
// ------------------------------------------------------------------
//
// Compact side-column card for history, access, summary, and secondary
// actions — the primitive `Version history` and `Share links` move into on
// `ReportDetailPage`'s right rail (story 2-130, not this one). The
// mapping doc's §4.4 finding is the reason this exists as its OWN primitive
// rather than a narrower `glass-card`: the pre-2-127 bug was a 7-column
// `Table` rendered inside a ~320px rail — `Table` is the wrong shape for a
// column that narrow (§3.3's table rule), and a card wrapper alone doesn't
// fix that, only a primitive whose CONTENT CONVENTION is stacked rows does.
//
// RailPanel itself stays content-agnostic (mapping doc §5 — presentational,
// no data): it renders a title/action header and a `children` slot. What
// goes inside (a stacked access card per share link, a version-row list) is
// each consumer's own composition, typically `DataList`/`EntityCard`
// (UI-DS-01, later) or, until those land, plain rows — RailPanel does not
// assume that shape.
//
// Visual treatment intentionally breaks from `.glass-card` (kept, unused
// here — see tokens.css's story-2-127 header comment): flat `--card`
// surface, hairline `--border`, small `--radius-sm` corners, a light
// shadow — the calmer paper direction this story's token retune adopted,
// demonstrated on a real primitive rather than only in the color-swatch
// section.
export interface RailPanelProps {
  title: string;
  /** Optional trailing action in the panel header (e.g. "New link", "See all"). Compose `Button`. */
  action?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function RailPanel({
  title,
  action,
  headingLevel = 3,
  children,
  className = '',
  'data-testid': testId,
}: RailPanelProps): JSX.Element {
  return (
    <section
      className={`rounded-hairline border border-border bg-card p-4 shadow-sm ${className}`}
      data-testid={testId}
    >
      <ContextHeader title={title} action={action} headingLevel={headingLevel} />
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
